import { Component, ViewChild, OnInit, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
  Observable,
  of,
  firstValueFrom,
  take,
  combineLatest,
  BehaviorSubject,
  Subject,
  Subscription,
  forkJoin,
  map,
  catchError,
  takeUntil,
  filter,
  switchMap,
} from 'rxjs';
import { Button } from 'ustore-ui';

import { CartProduct } from '../../core/models/cart.model';
import { ProductService } from '../../core/services/product.service';
import { ProductCategory } from '../../core/models/product.model';
import {
  UManagerSlugService,
  ChannelData,
} from '../../core/services/umanager-slug.service';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { User } from '../../core/models/user.model';
import {
  FixaResource,
  SelectedContractDetails,
} from '../../core/models/login.model';
import { CartItem } from '../../core/store/cart/cart.state';
import { UserFacade } from '../../core/store/user/user.facade';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import { ProductV2 } from '../../core/models/product-v2.model';
import { OrderService } from '../../core/services/order.service';
import { ToastService } from '../../shared/services/toast.service';
import { UrlService } from '../../core/services/url.service';
import { ExternalTermsService } from '../../core/services/external-terms.service';
import { OrderSuccessGuard } from '../../core/guards/order-success.guard';
import { OrderErrorGuard } from '../../core/guards/order-error.guard';
import { OrderPartialErrorService } from '../../core/services/order-partial-error.service';
import {
  CreateOrderRequest,
  CreateOrderProduct,
  CreateOrderPayment,
  OrderBillingAddress,
  OrderRequiredField,
} from '../../core/models/order.model';
import {
  PRESENCA_DIGITAL_UUIDS,
  REGISTRY_DOMAIN_UUIDS,
} from '../../shared/constants/variation-uuids.constant';
import { isEmailEmpresarialVariationUuid } from '../../core/utils/cart-domain-suggestion-rules';
import { EMAIL_DOMAIN_FIELD_NAME } from '../../shared/constants/domain-promo.constants';
import { StepperService } from '../../core/services/stepper.service';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { CartProductListComponent } from '../../shared/components/cart-product-list/cart-product-list.component';
import {
  StepperComponent,
  Step,
} from '../../shared/components/stepper/stepper.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { ChannelThemeService } from '../../core/services/channel-theme.service';
import { ChannelConfigService } from '../../core/services/channel-config.service';
import { SellerResponse } from '../../core/models/seller.model';
import { SellerService } from '../../core/services/seller.service';
import { SellerCodeStorageService } from '../../core/services/seller-code-storage.service';
import { ScheduleService } from '../../core/services/schedule.service';
import { ScheduleSlotsResponse } from '../../core/models/schedule.model';
import { MindHpsService } from '../../core/services/mind-hps.service';
import { MindCatalogStore } from '../../core/store/mind/mind-catalog.store';
import {
  MindOrderCreateRequest,
  MindOrderCreateResponse,
  MindOrderCustomer,
  MindOrderProduct,
  MindScheduleProduct,
} from '../../core/models/mind-order.model';
import { LocationFacade } from '../../core/store/location/location.facade';
import { SavedLocation } from '../../core/store/location/location.state';
import { MindService } from '../../core/services/mind.service';
import { CategoryHelperService } from '../../shared/services/category-helper.service';
import {
  isMceExternalChannelFlow,
  readExternalChannelAccessFromSession,
  readStoredExternalChannelOrigin,
} from '../../core/utils/external-channel-access.util';
import {
  buildInstallationAddress,
  buildMindCustomer,
  buildMindOrderCreateRequest,
  buildMindOrderProductFromCart,
  buildMindScheduleProductFromCart,
  mapScheduleSlotToOrderSchedule,
} from '../../core/utils/mind-order-payload.utils';
import { parseMindOrderMeta } from '../../core/utils/mind-price.utils';

import { ProductConfigCardComponent } from './product-config-card/product-config-card.component';
import { ProductLinkCardComponent } from './product-link-card/product-link-card.component';
import { CompanyCardComponent } from './company-card/company-card.component';
import { PaymentInvoiceCardComponent } from './payment-invoice-card/payment-invoice-card.component';
import { OrderSummaryCardComponent } from './order-summary-card/order-summary-card.component';
import { PaymentMethodCardComponent } from './payment-method-card/payment-method-card.component';
import { InstallationAddressCardComponent } from './installation-address-card/installation-address-card.component';
import {
  SchedulePickerCardComponent,
  ScheduleSelections,
} from './schedule-picker-card/schedule-picker-card.component';


interface MceInvoiceUnavailableItem {
  productName: string;
  planName: string;
}

interface MceAvailabilityResult {
  item: CartItem;
  planId: string;
  availableCount: number;
}

interface MceAvailabilityState {
  limits: Record<string, number>;
  unavailable: MceInvoiceUnavailableItem[];
  hasAdjustedQuantity: boolean;
}

@Component({
  selector: 'umkt-checkout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    IconComponent,
    CompanyCardComponent,
    ProductConfigCardComponent,
    ProductLinkCardComponent,
    PaymentInvoiceCardComponent,
    OrderSummaryCardComponent,
    LoadingComponent,
    CartProductListComponent,
    Button,
    StepperComponent,
    PaymentMethodCardComponent,
    SchedulePickerCardComponent,
    InstallationAddressCardComponent,
    I18nPipe,
  ],
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutComponent implements OnInit, OnDestroy {
  @ViewChild(CompanyCardComponent) companyCard!: CompanyCardComponent;
  @ViewChild(ProductConfigCardComponent)
  productConfigCard!: ProductConfigCardComponent;
  @ViewChild(PaymentInvoiceCardComponent)
  paymentInvoiceCard!: PaymentInvoiceCardComponent;
  @ViewChild(OrderSummaryCardComponent)
  orderSummary!: OrderSummaryCardComponent;
  @ViewChild(ProductLinkCardComponent)
  productLinkCard!: ProductLinkCardComponent;
  @ViewChild(InstallationAddressCardComponent)
  installationAddressCard!: InstallationAddressCardComponent;

  cartItems$: Observable<CartProduct[]> = of([]);
  cartTotal$: Observable<number> = of(0);
  currentUser$: Observable<User | null>;
  regionId$: Observable<string>;
  cartCount$: Observable<number>;
  cartLoading$: Observable<boolean>;
  availableOffersLoading$: Observable<boolean>;
  mockCartProducts$ = new BehaviorSubject<CartProduct[]>([]);
  private destroy$ = new Subject<void>();
  private channelData: ChannelData | null = null;
  private mindStore = inject(MindCatalogStore);

  isLoading = false;
  showCardForm = false;
  termsAccepted = false;
  microsoftTermsAccepted = false;
  showProductConfigCard = false;
  hasMindItemsInCart = false;
  hasMicrosoftItemsInCart = false;
  isMinhaClaroSelected = false;
  isFixaSelected = false;
  currentStepIndex = 0;
  visibleSteps: Step[] = [];
  showPaymentInvoiceCard = false;
  invoicePaymentType: 'minha_conta' | 'boleto_cartao' = 'boleto_cartao';
  showPaymentMethodCard = true;
  isPaymentMethodCardEditing = false;
  isPaymentInvoiceCardEditing = false;
  isProductLinkCardEditing = false;
  isInstallationAddressCardEditing = false;
  isScheduleCardEditing = false;
  selectedPaymentMethod: 'boleto' | 'credit_card' = 'boleto';
  selectedCardId: string | 'new' | null = 'new';
  microsoftTermsUrl = '';
  termsAndConditionsUrl =
    'https://mkt-image-front.s3.us-east-1.amazonaws.com/Termos+e+Condic%CC%A7o%CC%83es+Gerais.pdf';
  acceptanceTermId = '';
  isMobileLogin = false;
  isFixaLogin = false;
  isMceExternalFlow = false;
  mceShowLinkingInStepper = false;
  mceLinkingFromReview = false;
  mceQuantityLimitsByPlanId: Record<string, number> = {};
  mceInvoiceUnavailableItems: MceInvoiceUnavailableItem[] = [];
  mceShowAutoLinkWarning = false;
  private mceSyncingCartState = false;
  private mceOffersSyncComplete = false;
  private hasRoutedMceInitialStep = false;
  savedSellerCode: string | null = null;
  savedSellerData: SellerResponse | null = null;
  savedScheduleSelections: ScheduleSelections | null = null;
  scheduleSlotsResponse: ScheduleSlotsResponse | null = null;
  savedAddressDetails: { complement: string; reference: string } | null = null;
  savedInstallationForOrder: {
    homePass: string | null;
    installationAddress: { zipcode: string; number: string } | null;
  } | null = null;
  /** Dados extras do customer Mind (ex.: CPF responsável) quando a UI PME coletar. */
  savedMindCustomer: Partial<MindOrderCustomer> | null = null;

  private lastValidatedSvaPayload: CreateOrderRequest | null = null;
  private orderSummaryValidationRequestId = 0;

  stepList: Step[] = [];

  private computeVisibleSteps(): Step[] {
    let steps = [...this.stepList];

    if (!this.hasMindItemsInCart) {
      const installationLabel = this.i18nService.instant(
        'pages.checkout.steps.installationAddress'
      );
      const scheduleLabel = this.i18nService.instant(
        'pages.checkout.steps.schedule'
      );
      steps = steps.filter(
        s => s.label !== installationLabel && s.label !== scheduleLabel
      );
    }

    if (!this.showProductConfigCard) {
      const productLabel = this.i18nService.instant(
        'pages.checkout.steps.product'
      );
      steps = steps.filter(s => s.label !== productLabel);
    }

    const linkingLabel = this.i18nService.instant('pages.checkout.steps.linking');
    const hideLinkingStep =
      !this.isMinhaClaroSelected ||
      (this.isMceExternalFlow && (!this.isMobileLogin || !this.mceShowLinkingInStepper));

    if (hideLinkingStep) {
      const phoneLinkingIndex = steps.findIndex(s => s.label === linkingLabel);
      if (phoneLinkingIndex !== -1) {
        steps = steps.filter((_, index) => index !== phoneLinkingIndex);
      }
    }

    return steps;
  }

  private langSubscription?: Subscription;
  private cartFacade = inject(CartFacade);
  private userFacade = inject(UserFacade);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private orderService = inject(OrderService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private orderSuccessGuard = inject(OrderSuccessGuard);
  private orderErrorGuard = inject(OrderErrorGuard);
  private orderPartialErrorService = inject(OrderPartialErrorService);
  private urlService = inject(UrlService);
  private stepperService = inject(StepperService);
  private i18nService = inject(I18nService);
  private externalTermsService = inject(ExternalTermsService);
  private channelEventsService = inject(ChannelEventsService);
  private productService = inject(ProductService);
  private umanagerSlugService = inject(UManagerSlugService);
  private utmPreservationService = inject(UtmPreservationService);
  private channelThemeService = inject(ChannelThemeService);
  private channelBrandService = inject(ChannelConfigService);
  private scheduleService = inject(ScheduleService);
  private mindHpsService = inject(MindHpsService);
  private locationFacade = inject(LocationFacade);
  private mindService = inject(MindService);
  private categoryHelper = inject(CategoryHelperService);
  private sellerService = inject(SellerService);
  private sellerCodeStorage = inject(SellerCodeStorageService);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.currentUser$ = this.userFacade.getUserDetails();
    this.regionId$ = this.locationFacade.getRegionId();
    this.cartCount$ = this.cartFacade.getCartItemCount();
    this.cartLoading$ = this.cartFacade.getIsLoading();
    this.availableOffersLoading$ = this.availableOffersFacade.isLoading();
    this.visibleSteps = this.computeVisibleSteps();
    this.checkForConfigurableProducts();
  }

  ngOnInit(): void {
    this.isMceExternalFlow = this.detectMceExternalFlow();
    if (this.isMceExternalFlow) {
      this.applyMcePaymentDefaults();
    }

    this.loadSellerCodeFromStorage();

    this.checkAndLoadChannelData().then(() => {
      this.loadCategoriesByCatalog();
    });
    this.mindStore.catalog$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadCategoriesByCatalog();
    });
    this.userFacade.getUserUuid().subscribe(uuid => {
      if (uuid && !this.isMceExternalFlow) {
        this.userFacade.loadUserDetails(uuid);
      }
    });

    this.stepperService.currentStep$.subscribe(index => {
      this.currentStepIndex = index;
    });

   this.langSubscription = this.i18nService
    .getCurrentLanguage()
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {

      this.productService.clearCategoriesCache()
      
      this.loadCategoriesByCatalog();
      
      this.updateStepList();
      if (this.isMceExternalFlow) {
        this.hasRoutedMceInitialStep = false;
        this.mceOffersSyncComplete = false;
      }
      this.updateVisibleSteps(false);
      if (this.isMceExternalFlow) {
        this.syncMceInvoiceCartState();
      }
    });

    this.userFacade.getLoginData().subscribe(loginData => {
      this.isMobileLogin = !!(
        loginData?.resources && loginData.resources.length > 0
      );
      const firstResource =
        Array.isArray(loginData?.resources) && loginData.resources.length > 0
          ? loginData.resources[0]
          : null;
      this.isFixaLogin = !!(
        !this.isMceExternalFlow &&
        firstResource &&
        typeof firstResource === 'object' &&
        'customerAccountId' in firstResource
      );
      this.applyFixaPaymentRules();
      if (this.isMceExternalFlow) {
        this.updateVisibleSteps(false);
        return;
      }
      if (!this.isMobileLogin) {
        this.showPaymentMethodCard = false;
        this.showPaymentInvoiceCard = true;
        this.invoicePaymentType = 'boleto_cartao';
        this.updateVisibleSteps();
      }
    });

    combineLatest([
      this.userFacade.getSelectedContract(),
      this.userFacade.getSelectedContractDetails(),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([contract, details]) => {
        this.setFixaSelected(!!contract || !!details);
      });

    this.ensureCartItemsMappedToOffers();

    this.checkAvailableMsisdns();

    this.cartFacade
      .getCartItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isMceExternalFlow) {
          this.syncMceInvoiceCartState();
        }
      });

    this.updateVisibleSteps(!this.isMceExternalFlow);
  }

  private detectMceExternalFlow(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return isMceExternalChannelFlow(
      readExternalChannelAccessFromSession(),
      readStoredExternalChannelOrigin()
    );
  }

  private applyMcePaymentDefaults(): void {
    this.invoicePaymentType = 'minha_conta';
    this.isMinhaClaroSelected = true;
    this.selectedCardId = null;
    this.showPaymentMethodCard = true;
    this.showPaymentInvoiceCard = true;
    this.syncMceInvoiceCartState();
  }

  get hasMceInvoiceUnavailableItems(): boolean {
    return this.mceInvoiceUnavailableItems.length > 0;
  }

  get mceInvoiceUnavailableProductLabels(): string[] {
    return this.mceInvoiceUnavailableItems.map(item =>
      item.planName ? `${item.productName} - ${item.planName}` : item.productName
    );
  }

  private shouldApplyMceQuantityLimits(): boolean {
    return this.isMceExternalFlow && this.invoicePaymentType === 'minha_conta';
  }

  get isFixaPaymentFlow(): boolean {
    return this.isFixaLogin || this.isFixaSelected;
  }

  private applyFixaPaymentRules(): void {
    if (!this.isFixaPaymentFlow) {
      return;
    }

    this.invoicePaymentType = 'boleto_cartao';
    this.isMinhaClaroSelected = false;
    this.selectedCardId = this.selectedCardId ?? 'new';
    this.showPaymentMethodCard = false;
    this.showPaymentInvoiceCard = true;
    this.clearMceInvoiceState();
    this.updateVisibleSteps(false);
  }

  private restorePaymentMethodRules(): void {
    if (this.isFixaPaymentFlow) {
      return;
    }

    if (this.isMceExternalFlow) {
      this.applyMcePaymentDefaults();
      return;
    }

    if (this.isMobileLogin) {
      this.showPaymentMethodCard = true;
      this.showPaymentInvoiceCard = true;
      this.updateVisibleSteps(false);
    }
  }

  private showMceInsufficientLinesToast(): void {
    this.toastService.showError(
      this.i18nService.instant('pages.checkout.messages.insufficientLinesError'),
      5000,
      'top-center'
    );
  }

  private clearMceInvoiceActiveState(): void {
    this.mceQuantityLimitsByPlanId = {};
    this.mceShowAutoLinkWarning = false;
  }

  private clearMceInvoiceState(): void {
    this.clearMceInvoiceActiveState();
    this.mceInvoiceUnavailableItems = [];
  }

  private clearProductConfigSharedCache(): void {
    ProductConfigCardComponent.clearSharedFormDataCache();
  }

  private isInvoicePaymentBlocked(): boolean {
    return (
      this.isMceExternalFlow &&
      this.hasMceInvoiceUnavailableItems &&
      this.invoicePaymentType === 'minha_conta'
    );
  }

  private showInvoiceUnavailableErrorToast(): void {
    this.toastService.showError(
      this.i18nService.instant('pages.checkout.messages.invoiceUnavailableError'),
      5000,
      'top-center'
    );
  }

  private syncMceInvoiceCartState(): void {
    if (!this.isMceExternalFlow) {
      return;
    }

    if (this.mceSyncingCartState) {
      return;
    }

    this.availableOffersFacade
      .isLoading()
      .pipe(
        filter(loading => !loading),
        take(1),
        switchMap(() => this.cartFacade.getCartItems().pipe(take(1))),
        takeUntil(this.destroy$)
      )
      .subscribe(cartItems => {
        const itemsWithVariation = cartItems.filter(item => item.selectedVariation?.uuid);

        if (itemsWithVariation.length === 0) {
          this.clearMceInvoiceState();
          this.mceOffersSyncComplete = true;
          this.attemptMceInitialRouting();
          return;
        }

        this.mapCartItemsToMceOffers(itemsWithVariation);

        forkJoin(
          itemsWithVariation.map(item =>
            this.availableOffersFacade
              .getMsisdnsByVariationUuid(item.selectedVariation!.uuid)
              .pipe(
                take(1),
                map(msisdns => ({
                  item,
                  planId: item.selectedVariation!.uuid,
                  availableCount: msisdns.length,
                }))
              )
          )
        )
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe(results => {
            this.mceSyncingCartState = true;

            const previousUnavailable = [...this.mceInvoiceUnavailableItems];
            const applyQuantityLimits = this.shouldApplyMceQuantityLimits();
            const availabilityState = this.applyMceAvailabilityRules(
              results,
              applyQuantityLimits
            );

            if (applyQuantityLimits) {
              this.mceQuantityLimitsByPlanId = availabilityState.limits;
            } else {
              this.clearMceInvoiceActiveState();
            }

            this.mceInvoiceUnavailableItems = availabilityState.unavailable;

            const unavailableChanged = this.haveMceUnavailableItemsChanged(
              availabilityState.unavailable,
              previousUnavailable
            );

            if (applyQuantityLimits) {
              this.handleMceAutoLinkAfterSync(availabilityState.hasAdjustedQuantity);
            }

            if (unavailableChanged || availabilityState.hasAdjustedQuantity) {
              this.updateVisibleSteps(false);
            }

            this.mceSyncingCartState = false;
            this.mceOffersSyncComplete = true;
            this.attemptMceInitialRouting();
          });
      });
  }

  private mapCartItemsToMceOffers(items: CartItem[]): void {
    items.forEach(item => {
      if (item.product) {
        this.availableOffersFacade.mapProductVariationsToOffers(item.product);
      }
    });
  }

  private applyMceAvailabilityRules(
    results: MceAvailabilityResult[],
    applyQuantityLimits: boolean
  ): MceAvailabilityState {
    const limits: Record<string, number> = {};
    const unavailable: MceInvoiceUnavailableItem[] = [];
    let hasAdjustedQuantity = false;

    results.forEach(({ item, planId, availableCount }) => {
      if (applyQuantityLimits) {
        limits[planId] = availableCount;
      }

      const quantity = item.quantity ?? 1;

      if (availableCount === 0) {
        unavailable.push({
          productName: item.product?.name ?? 'Produto',
          planName: item.selectedVariation?.name ?? '',
        });
        return;
      }

      if (applyQuantityLimits && quantity > availableCount) {
        this.cartFacade.updateCartItemQuantity(item.id, availableCount);
        hasAdjustedQuantity = true;
      }
    });

    return { limits, unavailable, hasAdjustedQuantity };
  }

  private haveMceUnavailableItemsChanged(
    current: MceInvoiceUnavailableItem[],
    previous: MceInvoiceUnavailableItem[]
  ): boolean {
    return (
      current.length !== previous.length ||
      current.some(
        (item, index) =>
          item.productName !== previous[index]?.productName ||
          item.planName !== previous[index]?.planName
      )
    );
  }

  private handleMceAutoLinkAfterSync(hasAdjustedQuantity: boolean): void {
    if (hasAdjustedQuantity) {
      this.toastService.showWarning(
        this.i18nService.instant('pages.checkout.messages.quantityAdjustedForLines'),
        6000,
        'top-center'
      );
      this.tryAutoLinkMsisdnsForMce();
    } else if (!this.mceLinkingFromReview) {
      this.tryAutoLinkMsisdnsForMce();
    }
  }

  private attemptMceInitialRouting(): void {
    if (
      !this.isMceExternalFlow ||
      this.hasRoutedMceInitialStep ||
      this.mceLinkingFromReview ||
      !this.mceOffersSyncComplete
    ) {
      return;
    }

    this.cartFacade
      .getCartItems()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(cartItems => {
        if (cartItems.length === 0) {
          return;
        }

        this.syncConfigurableProductsState(cartItems);
        this.routeMceInitialStepIfReady();
      });
  }

  private routeMceInitialStepIfReady(): void {
    if (
      !this.isMceExternalFlow ||
      this.hasRoutedMceInitialStep ||
      this.mceLinkingFromReview ||
      this.visibleSteps.length === 0
    ) {
      return;
    }

    this.updateVisibleSteps(false);
    const targetIndex = this.getNextMceRequiredStepIndex(-1);
    if (targetIndex < 0) {
      return;
    }

    this.hasRoutedMceInitialStep = true;
    this.stepperService.goToStep(targetIndex);
  }

  private getMceRequiredStepLabels(): string[] {
    const requiredLabels: string[] = [];

    if (
      this.hasMceInvoiceUnavailableItems &&
      this.invoicePaymentType === 'minha_conta'
    ) {
      requiredLabels.push(this.i18nService.instant('pages.checkout.steps.payment'));
    }

    if (this.showProductConfigCard) {
      requiredLabels.push(this.i18nService.instant('pages.checkout.steps.product'));
    }

    return requiredLabels;
  }

  private getNextMceRequiredStepIndex(fromIndex: number): number {
    const requiredLabels = new Set(this.getMceRequiredStepLabels());
    const nextRequiredIndex = this.visibleSteps.findIndex(
      (step, index) => index > fromIndex && requiredLabels.has(step.label)
    );

    if (nextRequiredIndex >= 0) {
      return nextRequiredIndex;
    }

    return this.findVisibleStepIndexByLabel(
      this.i18nService.instant('pages.checkout.steps.review')
    );
  }

  private goToNextMceRequiredStepOrReview(): void {
    const targetIndex = this.getNextMceRequiredStepIndex(this.currentStepIndex);

    if (targetIndex >= 0 && targetIndex !== this.currentStepIndex) {
      this.stepperService.goToStep(targetIndex);
    } else {
      this.stepperService.next();
    }
  }

  private normalizeMsisdnList(msisdns: string[] | undefined): string[] {
    return (msisdns || []).map(msisdn => msisdn.replace(/\D/g, '')).filter(Boolean);
  }

  private msisdnListsEqual(a: string[], b: string[]): boolean {
    const left = [...this.normalizeMsisdnList(a)].sort();
    const right = [...this.normalizeMsisdnList(b)].sort();
    if (left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => value === right[index]);
  }

  private tryAutoLinkMsisdnsForMce(): void {
    if (!this.isMceExternalFlow || !this.isMinhaClaroSelected) {
      return;
    }

    if (this.mceLinkingFromReview || this.shouldShowPhoneLinking()) {
      return;
    }

    void this.autoLinkMsisdnsForMce();
  }

  private async autoLinkMsisdnsForMce(): Promise<boolean> {
    await firstValueFrom(
      this.availableOffersFacade.isLoading().pipe(
        filter(loading => !loading),
        take(1)
      )
    );

    const cartItems = await firstValueFrom(this.cartFacade.getCartItems().pipe(take(1)));
    const itemsWithVariation = cartItems.filter(item => item.selectedVariation?.uuid);

    if (itemsWithVariation.length === 0) {
      return true;
    }

    itemsWithVariation.forEach(item => {
      if (item.product) {
        this.availableOffersFacade.mapProductVariationsToOffers(item.product);
      }
    });

    const results = await firstValueFrom(
      forkJoin(
        itemsWithVariation.map(item =>
          this.availableOffersFacade
            .getMsisdnsByVariationUuid(item.selectedVariation!.uuid)
            .pipe(
              take(1),
              map(msisdns => ({ item, msisdns }))
            )
        )
      ).pipe(take(1))
    );

    const invoiceLinkableItemIds = new Set<string>();
    let hasAutoLinkedMsisdns = false;

    results.forEach(({ item, msisdns }) => {
      const needed = item.quantity;
      const available = this.normalizeMsisdnList(msisdns);
      const target = available.slice(0, needed);
      const current = this.normalizeMsisdnList(item.linkedMsisdns);
      const availableSet = new Set(available);
      const hasValidManualSelection =
        current.length === needed &&
        needed > 0 &&
        current.every(msisdn => availableSet.has(msisdn));

      if (available.length > 0) {
        invoiceLinkableItemIds.add(item.id);
      }

      if (hasValidManualSelection) {
        return;
      }

      if (target.length < needed) {
        return;
      }

      if (!this.msisdnListsEqual(current, target)) {
        this.cartFacade.updateCartItemLinkedMsisdns(item.id, target);
        hasAutoLinkedMsisdns = true;
      }
    });

    if (hasAutoLinkedMsisdns) {
      this.mceShowAutoLinkWarning = true;
    }

    const updatedCartItems = await firstValueFrom(this.cartFacade.getCartItems().pipe(take(1)));
    return this.hasRequiredLinkedMsisdnsForItems(updatedCartItems, invoiceLinkableItemIds);
  }

  private hasRequiredLinkedMsisdnsForItems(cartItems: CartItem[], itemIds: Set<string>): boolean {
    if (itemIds.size === 0) {
      return true;
    }

    return cartItems
      .filter(item => itemIds.has(item.id))
      .every(item => {
        const linkedCount = item.linkedMsisdns?.length || 0;
        return linkedCount === item.quantity;
      });
  }

  private getMceInvoiceLinkableItemIds(cartItems: CartItem[]): Set<string> {
    const itemIds = new Set<string>();

    cartItems.forEach(item => {
      const variationUuid = item.selectedVariation?.uuid;
      if (!variationUuid) {
        return;
      }

      if ((this.mceQuantityLimitsByPlanId[variationUuid] ?? 0) > 0) {
        itemIds.add(item.id);
      }
    });

    return itemIds;
  }

  private shouldEnsureMceAutoLinkBeforeNextStep(): boolean {
    if (!this.isMceExternalFlow || !this.isMinhaClaroSelected || this.mceLinkingFromReview) {
      return false;
    }

    const nextStep = this.visibleSteps[this.currentStepIndex + 1];
    return nextStep?.label === this.i18nService.instant('pages.checkout.steps.review');
  }

  onMceNavigateToLinkingStep(): void {
    this.mceLinkingFromReview = true;
    this.mceShowLinkingInStepper = true;
    this.mceShowAutoLinkWarning = false;
    this.updateVisibleSteps(false);

    const linkingIndex = this.findVisibleStepIndexByLabel(
      this.i18nService.instant('pages.checkout.steps.linking')
    );

    if (linkingIndex >= 0) {
      this.stepperService.goToStep(linkingIndex);
    }
  }

  onProductLinkNextStep(): void {
    if (this.mceLinkingFromReview) {
      if (!this.productLinkCard?.isFormValid()) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.linkingError'),
          5000,
          'top-center'
        );
        return;
      }
      this.completeMceLinkingEdit();
      return;
    }

    this.onNextStep();
  }

  onProductLinkPreviousStep(): void {
    if (this.mceLinkingFromReview) {
      this.completeMceLinkingEdit();
      return;
    }

    this.onPreviousStep();
  }

  private completeMceLinkingEdit(): void {
    this.mceLinkingFromReview = false;
    this.mceShowLinkingInStepper = false;
    this.updateVisibleSteps(false);

    const reviewIndex = this.findVisibleStepIndexByLabel(
      this.i18nService.instant('pages.checkout.steps.review')
    );

    if (reviewIndex >= 0) {
      this.stepperService.goToStep(reviewIndex);
    }
  }

  private validateMceLinkedMsisdns(): boolean {
    if (!this.isMceExternalFlow || !this.isMinhaClaroSelected) {
      return true;
    }

    const cartItems = this.getCartItems();
    const invoiceLinkableItemIds = this.getMceInvoiceLinkableItemIds(cartItems);

    if (!this.hasRequiredLinkedMsisdnsForItems(cartItems, invoiceLinkableItemIds)) {
      this.showMceInsufficientLinesToast();
      return false;
    }

    return true;
  }

  private updateStepList(): void {
    this.stepList = [
      {
        icon: 'mdn-Icon-empresa',
        label: this.i18nService.instant('pages.checkout.steps.company'),
      },
      {
        icon: 'mdn-Icon-localizacao',
        label: this.i18nService.instant(
          'pages.checkout.steps.installationAddress'
        ),
      },
      {
        icon: 'mdn-Icon-calendario',
        label: this.i18nService.instant('pages.checkout.steps.schedule'),
      },
      {
        icon: 'mdn-Icon-pagamento-digital',
        label: this.i18nService.instant('pages.checkout.steps.payment'),
      },
      {
        icon: 'mdn-Icon-pacote',
        label: this.i18nService.instant('pages.checkout.steps.product'),
      },
      {
        icon: 'mdn-Icon-engrenagem',
        label: this.i18nService.instant('pages.checkout.steps.linking'),
      },
      {
        icon: 'mdn-Icon-checkbox-circulo',
        label: this.i18nService.instant('pages.checkout.steps.review'),
      },
    ];
  }

  private checkAndLoadChannelData(): Promise<void> {
    return new Promise(resolve => {
      if (!isPlatformBrowser(this.platformId)) {
        this.channelData = null;
        this.channelBrandService.setChannelData(null);
        resolve();
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      let utmSource = urlParams.get('utm_source');

      if (!utmSource) {
        utmSource =
          this.utmPreservationService.preservedUtmParams?.['utm_source'] ||
          null;
      }

      if (!utmSource) {
        this.channelData = null;
        this.channelThemeService.resetToDefault();
        this.channelBrandService.setChannelData(null);
        resolve();
        return;
      }

      this.umanagerSlugService
        .checkSlugExists(utmSource)
        .pipe(takeUntil(this.destroy$), take(1))
        .subscribe({
          next: response => {
            if (response?.exists && utmSource) {
              this.umanagerSlugService
                .getChannelBySlug(utmSource)
                .pipe(takeUntil(this.destroy$), take(1))
                .subscribe({
                  next: channelData => {
                    if (
                      channelData?.status &&
                      channelData.status !== 'ACTIVE'
                    ) {
                      this.channelData = null;
                      this.utmPreservationService.disableUtmAndCleanUrl();
                      this.channelThemeService.resetToDefault();
                      this.channelBrandService.setChannelData(null);
                      resolve();
                      return;
                    }

                    this.channelData = channelData || null;
                    const parsedColors =
                      this.channelThemeService.parseChannelColors(
                        channelData?.colors ?? null
                      );
                    if (parsedColors) {
                      this.channelThemeService.applyChannelColors(parsedColors);
                    } else {
                      this.channelThemeService.resetToDefault();
                    }
                    this.channelBrandService.setChannelData(channelData);
                    resolve();
                  },
                  error: error => {
                    console.error('Error loading channel data:', error);
                    resolve();
                  },
                });
            } else {
              this.channelData = null;
              this.channelBrandService.setChannelData(null);
              resolve();
            }
          },
          error: error => {
            console.error('Error checking slug existence:', error);
            resolve();
          },
        });
    });
  }

  private loadCategoriesForEmptyCart(): void {
    this.productService
      .getCategories()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: resp => {

          const categories =
            this.categoryHelper.filterAndSortCategories(
              resp.data,
              this.channelData?.categoriesUuid
            );

          const cartProducts = this.convertCategoriesToCartProducts(categories);
          this.mockCartProducts$.next(cartProducts);
        },
        error: error => {
          console.error('Error loading categories for empty cart:', error);
          this.mockCartProducts$.next([]);
        },
      });
  }

  private getChannelCatalogs(): string[] {
    if (!this.channelData?.catalogs) return [];
    return this.channelData.catalogs.map((c: string) => c.toUpperCase());
  }

  private loadCategoriesByCatalog(): void {
    const catalogs = this.channelData?.catalogs?.length
      ? this.getChannelCatalogs()
      : [];

    const hasSva = catalogs.includes('SVA');
    const hasMind = catalogs.includes('MIND');

    if (!this.channelData || catalogs.length === 0) {
      this.loadCategoriesForEmptyCart();
      return;
    }

    if (hasSva && hasMind) {
      this.getCategoriesCombined();
      return;
    }

    if (hasSva) {
      this.loadCategoriesForEmptyCart();
      return;
    }

    if (hasMind) {
      this.getCategoriesMind();
      return;
    }
  }

  private getCategoriesMind(): void {
    const catalog = this.mindStore.snapshot;

    if (!catalog?.groups?.length) {
      this.mockCartProducts$.next([]);
      return;
    }

    const mindCategories = catalog.groups
      .filter(group => group.items && group.items.length > 0) 
      .map(group => ({
        name: group.title,
        uuid: `mind-${group.groupId}`,
        hasProducts: true,
      }));

    this.mockCartProducts$.next(
      this.convertCategoriesToCartProducts(mindCategories)
    );
  }

  private getCategoriesCombined(): void {
    this.productService
      .getCategories()
      .pipe(take(1))
      .subscribe({
        next: resp => {
          let svaCategories = resp.data;
          if (this.channelData?.categoriesUuid?.length) {
            svaCategories = svaCategories.filter(cat =>
              this.channelData!.categoriesUuid.includes(cat.uuid)
            );
          }

          const catalog = this.mindStore.snapshot;
          const mindCategories = (catalog?.groups ?? [])
            .filter(group => group.items && group.items.length > 0) 
            .map(group => ({
              name: group.title,
              uuid: `${group.groupId}`,
              hasProducts: true,
            }));

          const merged = [...svaCategories, ...mindCategories];

          this.mockCartProducts$.next(
            this.convertCategoriesToCartProducts(merged)
          );
        },
        error: () => this.mockCartProducts$.next([]),
      });
  }

  private convertCategoriesToCartProducts(
    categories: Pick<ProductCategory, 'uuid' | 'name' | 'hasProducts'>[]
  ): CartProduct[] {
    return categories.map(category => {
      const categoryName = category.name.toLowerCase();
      let iconCategory = 'business'; // default

      if (
        categoryName.includes('colaboração') ||
        categoryName.includes('produtividade')
      ) {
        iconCategory = 'office';
      } else if (categoryName.includes('segurança')) {
        iconCategory = 'security';
      } else if (
        categoryName.includes('presença') ||
        categoryName.includes('digital')
      ) {
        iconCategory = 'digitalPresence';
      } else if (
        categoryName.includes('gestão') ||
        categoryName.includes('benefícios') ||
        categoryName.includes('empresariais')
      ) {
        iconCategory = 'business';
      } else if (categoryName.includes('cloud')) {
        iconCategory = 'cloud';
      }

      return {
        id: category.uuid,
        categories: [iconCategory],
        name: category.name,
        subtotal: 0,
        plans: [],
        termsUrl: null,
      };
    });
  }

  private loadScheduleSlots(homePassFromEvent?: string | null): void {
    const selectedHomePass =
      homePassFromEvent !== undefined
        ? homePassFromEvent
        : this.installationAddressCard?.selectedHp != null
          ? String(this.installationAddressCard.selectedHp.hp)
          : null;

    combineLatest([
      this.currentUser$.pipe(take(1)),
      this.cartFacade.getCartItems().pipe(take(1)),
      this.regionId$.pipe(take(1)),
      this.locationFacade.location$.pipe(take(1)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, cartItems, regionId, installationLocation]) => {
        if (!this.hasMindItemsInCart) {
          this.scheduleSlotsResponse = {
            insufficientSlots: true,
            slots: [],
          };
          return;
        }
        if (!user) {
          this.scheduleSlotsResponse = {
            insufficientSlots: true,
            slots: [],
          };
          return;
        }
        const mindItems = cartItems.filter(
          item => item.product?.productType === 'MIND'
        );
        const payload = this.scheduleService.buildSchedulePayload({
          regionId,
          installationLocation,
          installationAddressFallback: user.installAddress ?? null,
          homePass: selectedHomePass,
          products: this.buildMindScheduleProducts(mindItems),
        });
        if (!payload) {
          this.scheduleSlotsResponse = {
            insufficientSlots: true,
            slots: [],
          };
          return;
        }
        this.scheduleService
          .getAvailableSlots(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: response => {
              this.scheduleSlotsResponse = response;
            },
            error: () => {
              this.scheduleSlotsResponse = {
                insufficientSlots: true,
                slots: [],
              };
            },
          });
      });
  }

  private loadMindHpsOnCheckoutIfUtmMind(): void {
    if (!this.utmPreservationService.isUtmEnabled()) {
      return;
    }
    this.cartFacade
      .getCartItems()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(items => {
        if (!items.some(item => this.isMindCartItem(item))) {
          return;
        }
        this.userFacade
          .getUserDetails()
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe(user => {
            const cep = user?.installAddress?.zipCode
              ? String(user.installAddress.zipCode).replace(/\D/g, '')
              : user?.addresses?.[0]?.zipCode?.replace(/\D/g, '');
            const number =
              user?.installAddress?.number != null
                ? String(user.installAddress.number)
                : user?.addresses?.[0]?.number;
            if (cep && number) {
              this.mindHpsService
                .getHps(cep, number)
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            }
          });
      });
  }

  get fetchMindInstallationHps(): boolean {
    return this.utmPreservationService.isUtmEnabled() && this.hasMindItemsInCart;
  }

  private ensureCartItemsMappedToOffers(): void {
    this.cartFacade
      .getCartItems()
      .pipe(take(1))
      .subscribe((cartItems: CartItem[]) => {
        if (cartItems.length === 0) return;

        const productsMap = new Map<string, ProductV2>();

        cartItems.forEach(item => {
          if (item.product && !productsMap.has(item.product.uuid)) {
            productsMap.set(item.product.uuid, item.product);
          }
        });

        productsMap.forEach(product => {
          this.availableOffersFacade.mapProductVariationsToOffers(product);
        });
      });
  }

  private checkAvailableMsisdns(): void {
    this.availableOffersFacade
      .isLoading()
      .pipe(
        filter(loading => !loading),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyAvailableMsisdnsFromStore();
      });
  }

  private applyAvailableMsisdnsFromStore(): void {
    if (this.isMceExternalFlow) {
      this.syncMceInvoiceCartState();
    }

    this.availableOffersFacade
      .hasAvailableMsisdns()
      .pipe(take(1))
      .subscribe(hasMsisdns => {
        if (!hasMsisdns && this.isMobileLogin && !this.isMceExternalFlow) {
          this.showPaymentMethodCard = false;
          this.showPaymentInvoiceCard = true;
          this.invoicePaymentType = 'boleto_cartao';
          this.isMinhaClaroSelected = false;
          this.updateVisibleSteps();
        }
      });
  }

  updateVisibleSteps(resetIndex: boolean = true): void {
    this.visibleSteps = this.computeVisibleSteps();
    this.stepperService.setSteps(this.visibleSteps.length, resetIndex);
  }

  setMinhaClaroSelected(value: boolean): void {
    if (this.isMinhaClaroSelected !== value) {
      this.isMinhaClaroSelected = value;
      this.updateVisibleSteps(false);

      if (this.isMceExternalFlow) {
        if (value) {
          this.syncMceInvoiceCartState();
        } else {
          this.clearMceInvoiceActiveState();
        }
      }
    }
  }

  setFixaSelected(value: boolean): void {
    if (this.isFixaSelected !== value) {
      this.isFixaSelected = value;
      if (this.isFixaPaymentFlow) {
        this.applyFixaPaymentRules();
      } else {
        this.restorePaymentMethodRules();
      }
      this.updateVisibleSteps(false);
    }
  }

  private loadSellerCodeFromStorage(): void {
    if (this.savedSellerCode) {
      return;
    }

    const codeFromUrl =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('sellerCode')
        : null;
    const code = (codeFromUrl || this.sellerCodeStorage.get() || '')
      .trim()
      .toUpperCase();

    if (!code) {
      return;
    }

    this.sellerCodeStorage.set(code);
    this.savedSellerCode = code;

    this.sellerService.getSellerByCode(code).subscribe({
      next: (response: SellerResponse) => {
        if (!response?.error && response?.data) {
          this.savedSellerData = response;
        }
      },
      error: () => {
        // Mantém o código preenchido; a validação do card trata vendedor inválido
      },
    });
  }

  async onNextStep(): Promise<void> {
    const isValid = this.validateCurrentStep();
    if (isValid) {
      if (this.shouldShowPaymentCard() && this.paymentInvoiceCard) {
        this.savedSellerCode =
          this.paymentInvoiceCard.sellerForm?.get('sellerCode')?.value ?? null;
        this.savedSellerData = this.paymentInvoiceCard.sellerData ?? null;
      }

      if (this.shouldEnsureMceAutoLinkBeforeNextStep()) {
        const hasEnoughLines = await this.autoLinkMsisdnsForMce();
        if (!hasEnoughLines) {
          this.showMceInsufficientLinesToast();
          return;
        }
      }

      this.stepperService.next();
    }
  }

  onPreviousStep(): void {
    this.stepperService.previous();
  }

  onInvoicePaymentTypeChange(type: 'minha_conta' | 'boleto_cartao'): void {
    if (this.isFixaPaymentFlow && type === 'minha_conta') {
      this.applyFixaPaymentRules();
      return;
    }

    if (
      type === 'minha_conta' &&
      this.isMceExternalFlow &&
      this.hasMceInvoiceUnavailableItems
    ) {
      this.showInvoiceUnavailableErrorToast();
      return;
    }

    const previousType = this.invoicePaymentType;

    this.invoicePaymentType = type;
    if (type === 'minha_conta') {
      this.setMinhaClaroSelected(true);
      this.selectedCardId = null;
    } else {
      this.setMinhaClaroSelected(false);
      if (previousType === 'minha_conta' && this.isLastStep()) {
        this.isPaymentInvoiceCardEditing = true;
      }
    }
  }

  onShowPaymentInvoice(): void {
    this.showPaymentInvoiceCard = true;
  }

  onBackToPaymentMethod(): void {
    this.showPaymentInvoiceCard = false;
  }

  onPaymentMethodCardEditingStateChange(isEditing: boolean): void {
    this.isPaymentMethodCardEditing = isEditing;
  }

  onPaymentInvoiceCardEditingStateChange(isEditing: boolean): void {
    this.isPaymentInvoiceCardEditing = isEditing;
  }

  onPaymentMethodChange(method: 'boleto' | 'credit_card'): void {
    this.selectedPaymentMethod = method;
  }

  onTokenCardSelected(cardId: string | 'new'): void {
    this.selectedCardId = cardId;
  }

  onProductLinkCardEditingStateChange(isEditing: boolean): void {
    this.isProductLinkCardEditing = isEditing;
  }

  onInstallationAddressCardEditingStateChange(isEditing: boolean): void {
    this.isInstallationAddressCardEditing = isEditing;
  }

  onScheduleCardEditingStateChange(isEditing: boolean): void {
    this.isScheduleCardEditing = isEditing;
  }

  onScheduleSubmit(selections: ScheduleSelections): void {
    this.savedScheduleSelections = selections;
    if (this.shouldShowCompleted()) {
      this.isScheduleCardEditing = false;
    } else {
      this.onNextStep();
    }
  }

  onMindCustomerChange(customer: Partial<MindOrderCustomer>): void {
    this.savedMindCustomer = customer;
  }

  /** Com MIND: exige company card válido + CPF responsável (11 dígitos) no snapshot. */
  private isCompanyStepValid(): boolean {
    if (!this.companyCard?.isFormValid) {
      return false;
    }
    if (!this.hasMindItemsInCart) {
      return true;
    }
    const snapshot =
      this.companyCard.getMindCustomerSnapshot?.() ?? this.savedMindCustomer;
    if (snapshot) {
      this.savedMindCustomer = snapshot;
    }
    const cpf = (snapshot?.cpf ?? '').replace(/\D/g, '');
    const contactName = (snapshot?.contactName ?? '').trim();
    const email = (snapshot?.email ?? '').trim();
    return (
      cpf.length === 11 &&
      contactName.length > 0 &&
      email.length > 0
    );
  }

  onAddressDetailsSubmit(details: {
    complement: string;
    reference: string;
    homePass: string | null;
    installationAddress: { zipcode: string; number: string };
  }): void {
    this.savedAddressDetails = {
      complement: details.complement,
      reference: details.reference,
    };
    this.savedInstallationForOrder = {
      homePass: details.homePass,
      installationAddress:
        details.installationAddress?.zipcode && details.installationAddress?.number
          ? details.installationAddress
          : null,
    };
    this.loadScheduleSlots(details.homePass);
  }

  validateCurrentStep(): boolean {
    const currentLabel = this.visibleSteps[this.currentStepIndex]?.label;

    if (
      currentLabel === this.i18nService.instant('pages.checkout.steps.company')
    ) {
      if (!this.isCompanyStepValid()) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.companyError'),
          5000,
          'top-center'
        );
        return false;
      }
    }

    if (
      currentLabel === this.i18nService.instant('pages.checkout.steps.product')
    ) {
      if (!this.productConfigCard?.isFormValid()) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.configDomainError'),
          5000,
          'top-center'
        );
        return false;
      }
      if (!this.productConfigCard?.areAllDomainsValid()) {
        this.toastService.showError(
          this.i18nService.instant(
            'pages.checkout.messages.configValidateError'
          ),
          5000,
          'top-center'
        );
        return false;
      }
    }

    if (
      currentLabel === this.i18nService.instant('pages.checkout.steps.payment')
    ) {
      if (this.isInvoicePaymentBlocked()) {
        this.showInvoiceUnavailableErrorToast();
        return false;
      }

      if (!this.paymentInvoiceCard?.showCheck) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.paymentError'),
          5000,
          'top-center'
        );
        return false;
      }
    }

    if (
      currentLabel === this.i18nService.instant('pages.checkout.steps.linking')
    ) {
      if (!this.productLinkCard?.isFormValid()) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.linkingError'),
          5000,
          'top-center'
        );
        return false;
      }
    }

    if (
      currentLabel ===
      this.i18nService.instant('pages.checkout.steps.installationAddress')
    ) {
      if (!this.installationAddressCard?.isFormValid) {
        this.toastService.showError(
          this.i18nService.instant(
            'pages.checkout.messages.installationAddressError'
          ),
          5000,
          'top-center'
        );
        return false;
      }
    }

    return true;
  }

  shouldShowCompanyCard(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.company')
    );
  }

  shouldShowPaymentCard(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.payment')
    );
  }

  shouldShowInstallationAddressCard(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.installationAddress')
    );
  }

  shouldShowProductConfig(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.product')
    );
  }

  shouldShowScheduleCard(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.schedule')
    );
  }

  shouldShowPhoneLinking(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.linking')
    );
  }

  shouldShowCompleted(): boolean {
    return (
      this.visibleSteps[this.currentStepIndex]?.label ===
      this.i18nService.instant('pages.checkout.steps.review')
    );
  }

  canGoToNextStep(): boolean {
    return this.currentStepIndex < this.visibleSteps.length - 1;
  }

  canGoToPreviousStep(): boolean {
    return this.currentStepIndex > 0;
  }

  isLastStep(): boolean {
    return this.shouldShowCompleted();
  }

  private loadMicrosoftTerms(): void {
    this.externalTermsService.getTermsByType('PURCHASE', 'Microsoft').subscribe({
      next: term => {
        if (term.fileUrl) {
          this.microsoftTermsUrl = term.fileUrl;
        }
        if (term.id) {
          this.acceptanceTermId = term.id;
        }
      },
      error: error => {
        console.error('Erro ao carregar termos:', error);
        this.acceptanceTermId = '';
      },
    });
  }

  onMicrosoftTermsAcceptedChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.microsoftTermsAccepted = target.checked;
  }

  private checkForConfigurableProducts(): void {
    this.cartFacade.getCartItems().subscribe((cartItems: CartItem[]) => {
      const previousHasMind = this.hasMindItemsInCart;
      const { productConfigChanged, mindChanged } =
        this.syncConfigurableProductsState(cartItems);

      if (productConfigChanged || mindChanged) {
        this.updateVisibleSteps(false);
      }

      if (this.isMceExternalFlow) {
        this.attemptMceInitialRouting();
      }

      if (
        !previousHasMind &&
        this.hasMindItemsInCart &&
        this.utmPreservationService.isUtmEnabled()
      ) {
        this.loadMindHpsOnCheckoutIfUtmMind();
      }
    });
  }

  private syncConfigurableProductsState(cartItems: CartItem[]): {
    productConfigChanged: boolean;
    mindChanged: boolean;
  } {
    const previousShowProductConfig = this.showProductConfigCard;
    const previousHasMind = this.hasMindItemsInCart;
    const previousHasMicrosoft = this.hasMicrosoftItemsInCart;

    this.showProductConfigCard = cartItems.some(item =>
      this.hasRequiredDomainFields(item)
    );
    this.hasMindItemsInCart = cartItems.some(
      item => item.product?.productType === 'MIND'
    );
    this.hasMicrosoftItemsInCart = cartItems.some(item =>
      this.hasMicrosoftDomainField(item)
    );

    if (this.hasMicrosoftItemsInCart && !previousHasMicrosoft) {
      this.loadMicrosoftTerms();
    }

    return {
      productConfigChanged: previousShowProductConfig !== this.showProductConfigCard,
      mindChanged: previousHasMind !== this.hasMindItemsInCart,
    };
  }

  private findVisibleStepIndexByLabel(label: string): number {
    return this.visibleSteps.findIndex(step => step.label === label);
  }

  private revalidateOrderSummaryIfNeeded(): void {
    this.onOrderSummaryItemRemoved();
  }

  private hasMicrosoftDomainField(item: CartItem): boolean {
    return !!(
      item.product?.requiredFields &&
      item.product.requiredFields.some(
        (field: { name: string }) => field.name === 'microsoft_domain'
      )
    );
  }

  private hasRequiredDomainFields(item: CartItem): boolean {
    if (
      item.product.requiredFields &&
      Array.isArray(item.product.requiredFields)
    ) {
      const hasGoogleOrMicrosoft = item.product.requiredFields.some(
        (field: { name: string }) =>
          field.name === 'google_ws_domain' || field.name === 'microsoft_domain'
      );
      if (hasGoogleOrMicrosoft) return true;
    }

    if (item.selectedVariation?.requiredFields) {
      const hasVariationDomainField = item.selectedVariation.requiredFields.some(
        (field: { name: string }) =>
          field.name === 'com_br_domain' ||
          field.name === 'com_domain' ||
          field.name === 'net_domain' ||
          field.name === EMAIL_DOMAIN_FIELD_NAME
      );
      if (hasVariationDomainField) return true;
    }

    const variationUuid = item.selectedVariation?.uuid;
    if (variationUuid && REGISTRY_DOMAIN_UUIDS.includes(variationUuid)) {
      return true;
    }

    if (isEmailEmpresarialVariationUuid(variationUuid)) {
      return true;
    }

    if (item.selectedVariation?.name) {
      const variationName = item.selectedVariation.name.toLowerCase();
      return (
        variationName.includes('.com.br') ||
        variationName.includes('.com') ||
        variationName.includes('.net')
      );
    }

    return false;
  }

  onApplyCoupon(): void {
    this.toastService.showSuccess(
      this.i18nService.instant('pages.checkout.messages.couponSuccess'),
      3000,
      'top-center'
    );
  }

  onRemovePlan(ev: { productId: string; planId: string }) {
    // Método para compatibilidade com cart-product-list
    console.log('Remove plan from checkout:', ev);
  }

  onChangeQty(ev: { productId: string; planId: string; quantity: number }) {
    // Método para compatibilidade com cart-product-list
    console.log('Change quantity in checkout:', ev);
  }

  onOrderSummaryItemRemoved(): void {
    const requestId = ++this.orderSummaryValidationRequestId;

    try {
      const orderRequest = this.buildCreateOrderRequest();
      if (
        orderRequest.type !== 'SVA_ORDER' ||
        !this.shouldValidateOrderForDomainRegistration(orderRequest.orderRequest?.products)
      ) {
        return;
      }

      this.orderService.validateOrder(orderRequest.orderRequest).subscribe({
        next: validatedPayload => {
          if (requestId === this.orderSummaryValidationRequestId) {
            this.lastValidatedSvaPayload = this.mergeOwnershipFieldsIntoSvaPayload(
              validatedPayload,
              orderRequest.orderRequest.products
            );
          }
        },
        error: err => {
          console.error('Erro ao validar pedido após remoção do carrinho:', err);
        },
      });
    } catch (error) {
      console.error('Erro ao montar payload para validação após remoção do carrinho:', error);
    }
  }

  trackByProductId(index: number, product: CartProduct): string {
    return product.id;
  }

  async onSubmit(): Promise<void> {
    const currentUser = await firstValueFrom(this.userFacade.getUserDetails());
    if (!currentUser || !currentUser.document) {
      this.toastService.showError(
        this.i18nService.instant('pages.checkout.messages.cnpjError'),
        5000,
        'top-center'
      );
      this.router.navigate(['/register-movel'], {
        queryParams: { returnUrl: '/checkout' },
      });
      return;
    }

    if (!this.termsAccepted) {
      this.toastService.showError(
        this.i18nService.instant('pages.checkout.messages.termsError'),
        5000,
        'top-center'
      );
      return;
    }

    if (this.isMceExternalFlow && this.isMinhaClaroSelected) {
      const hasEnoughLines = await this.autoLinkMsisdnsForMce();
      if (!hasEnoughLines) {
        this.showMceInsufficientLinesToast();
        return;
      }
    }

    if (!this.validateChildComponents()) {
      return;
    }

    this.isLoading = true;

    try {
      const mindItems = this.getMindCartItems();
      const svaItems = this.getSvaCartItems();
      const hasMind = mindItems.length > 0;
      const hasSva = svaItems.length > 0;

      if (hasMind && hasSva) {
        const mindRequest = this.buildMindOrderRequest();
        const freshSvaRequest = this.buildSvaOrderRequest(svaItems);
        const svaRequest = this.mergeOwnershipFieldsIntoSvaPayload(
          this.lastValidatedSvaPayload ?? freshSvaRequest,
          freshSvaRequest.products
        );
        const mind$ = this.mindService.createOrder(mindRequest).pipe(
          map((data: MindOrderCreateResponse) => ({ success: true as const, data })),
          catchError((err: unknown) => of({ success: false as const, error: err, type: 'mind' as const }))
        );
        const sva$ = this.orderService.createOrder(svaRequest).pipe(
          map((data: unknown) => ({ success: true as const, data })),
          catchError((err: unknown) => of({ success: false as const, error: err, type: 'sva' as const }))
        );
        forkJoin({ mind: mind$, sva: sva$ }).subscribe({
          next: ({ mind, sva }) => {
            this.isLoading = false;
            const mindOk = mind.success;
            const svaOk = sva.success;

            if (mindOk && svaOk) {
              this.sendMindChannelEvents(mind.data, mindItems);
              this.sendChannelEvents(sva.success ? sva.data : undefined, svaRequest, svaItems);
              this.clearProductConfigSharedCache();
              this.cartFacade.clearCart();
              this.orderSuccessGuard.grantAccess();
              this.router.navigate(['/order-success']);
              return;
            }

            if (mindOk && !svaOk) {
              this.sendMindChannelEvents(mind.data, mindItems);
              this.sendOrderFailedEvent(
                'SVA',
                svaItems,
                (sva as { error: unknown }).error
              );
              mindItems.forEach(item => this.cartFacade.removeFromCart(item.id));
              this.orderPartialErrorService.setPartialError(svaItems, (sva as { error: unknown }).error);
              this.router.navigate(['/order-partial-error']);
              return;
            }

            if (!mindOk && svaOk) {
              this.sendChannelEvents(sva.success ? sva.data : undefined, svaRequest, svaItems);
              this.sendOrderFailedEvent(
                'MIND',
                mindItems,
                (mind as { error: unknown }).error
              );
              svaItems.forEach(item => this.cartFacade.removeFromCart(item.id));
              this.orderPartialErrorService.setPartialError(mindItems, (mind as { error: unknown }).error);
              this.router.navigate(['/order-partial-error']);
              return;
            }

            console.error('Erro ao criar pedido (mind/sva): ambos falharam');
            this.sendOrderFailedEvent('SVA_AND_MIND', [...mindItems, ...svaItems], {
              mind: (mind as { error?: unknown }).error,
              sva: (sva as { error?: unknown }).error,
            });
            this.orderErrorGuard.grantAccess();
            this.router.navigate(['/order-error']);
          },
        });
        return;
      }

      const orderRequest = this.buildCreateOrderRequest();

      if (orderRequest.type === 'SVA_ORDER') {
        const svaPayload = this.mergeOwnershipFieldsIntoSvaPayload(
          this.lastValidatedSvaPayload ?? orderRequest.orderRequest,
          orderRequest.orderRequest.products
        );
        this.orderService.createOrder(svaPayload).subscribe({
          next: response => {
            this.sendChannelEvents(response, svaPayload);
            this.lastValidatedSvaPayload = null;
            this.clearProductConfigSharedCache();
            this.cartFacade.clearCart();
            this.isLoading = false;
            this.orderSuccessGuard.grantAccess();
            this.router.navigate(['/order-success']);
          },
          error: error => {
            console.error('Erro ao criar pedido:', error);
            this.sendOrderFailedEvent('SVA', this.getSvaCartItems(), error);
            this.isLoading = false;
            this.orderErrorGuard.grantAccess();
            this.router.navigate(['/order-error']);
          },
        });
      } else { 
        this.mindService.createOrder(orderRequest.orderRequest).subscribe({
          next: _response => {
            this.sendMindChannelEvents(_response as MindOrderCreateResponse);
            this.clearProductConfigSharedCache();
            this.cartFacade.clearCart();
            this.isLoading = false;
            this.orderSuccessGuard.grantAccess();
            this.router.navigate(['/order-success']);
          },
          error: err => {
            console.error('Erro ao criar pedido:', err);
            this.sendOrderFailedEvent('MIND', this.getMindCartItems(), err);
            this.isLoading = false;
            this.orderErrorGuard.grantAccess();
            this.router.navigate(['/order-error']);
          },
        });
      }
    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      this.sendOrderFailedEvent('SVA_AND_MIND', this.getCartItems(), error);
      this.isLoading = false;
      this.orderErrorGuard.grantAccess();
      this.router.navigate(['/order-error']);
    }
  }

  onProductConfigValidateOrderRequest(payload: { isLastStep: boolean }): void {
    const isLastStep = payload?.isLastStep ?? false;
    const onSuccess = () => {
      if (this.productConfigCard) {
        if (isLastStep) {
          this.productConfigCard.completeSaveAfterValidate();
        } else {
          this.productConfigCard.completeAndGoToNextStep();
        }
      }
    };
    const onError = () => {
      this.productConfigCard?.setValidateOrderError();
      this.toastService.showError(
        this.i18nService.instant('pages.checkout.messages.validateOrderError') ?? 'Falha ao validar pedido.',
        5000,
        'top-center'
      );
    };

    if (!this.productConfigCard) {
      return;
    }
    try {
      const orderRequest = this.buildCreateOrderRequest();
      if (orderRequest.type !== 'SVA_ORDER') {
        onSuccess();
        return;
      }

      // Guarda o payload montado com forms ainda vivos (inclui ownership flags).
      // Domínio Google "existing" não passa por /validate-order; sem isso o create perde
      // confirmedNoOtherGoogleAccount / selfConfirmedOwnership após limpar o FormArray.
      if (
        this.shouldValidateOrderForDomainRegistration(orderRequest.orderRequest?.products)
      ) {
        this.orderService.validateOrder(orderRequest.orderRequest).subscribe({
          next: validatedPayload => {
            this.lastValidatedSvaPayload = this.mergeOwnershipFieldsIntoSvaPayload(
              validatedPayload,
              orderRequest.orderRequest.products
            );
            onSuccess();
          },
          error: err => {
            onError();
            console.error('Erro ao validar pedido:', err);
          },
        });
      } else {
        this.lastValidatedSvaPayload = orderRequest.orderRequest;
        onSuccess();
      }
    } catch (error) {
      console.error('Erro ao validar pedido:', error);
      onError();
    }
  }

  get validatedSvaOrderPayload(): CreateOrderRequest | null {
    return this.lastValidatedSvaPayload;
  }

  private get orderEventMetodoPagamento(): string {
    if (this.invoicePaymentType === 'minha_conta') {
      return 'MINHA_CONTA';
    }
    if (this.selectedPaymentMethod === 'credit_card') {
      return 'CREDIT_CARD';
    }
    return 'BOLETO';
  }

  private mapCartItemsToOrderEventProducts(cartItems: CartItem[]) {
    return cartItems.map(item => {
      const amount =
        item.selectedVariation?.salesPrice ??
        item.selectedVariation?.price ??
        item.product.salesPrice ??
        item.product.price ??
        0;

      return {
        productName: item.product.name || '',
        variationName: item.selectedVariation?.name || '',
        variationUuid: item.selectedVariation?.uuid || '',
        quantity: item.quantity,
        amount,
      };
    });
  }

  private extractOrderErrorMessage(error: unknown): {
    errorMessage?: string;
    errorCode?: string | number;
  } {
    const err = error as {
      status?: number;
      message?: string;
      error?: { msg?: string; message?: string; errors?: string[] };
    } | null;

    const errorMessage =
      err?.error?.msg ||
      err?.error?.message ||
      err?.error?.errors?.[0] ||
      err?.message ||
      undefined;

    return {
      errorMessage: typeof errorMessage === 'string' ? errorMessage : undefined,
      errorCode: err?.status,
    };
  }

  private sendOrderFailedEvent(
    orderType: 'SVA' | 'MIND' | 'SVA_AND_MIND',
    cartItems: CartItem[],
    error?: unknown
  ): void {
    const products = this.mapCartItemsToOrderEventProducts(cartItems);
    const total = products.reduce(
      (sum, prod) => sum + prod.amount * prod.quantity,
      0
    );
    const { errorMessage, errorCode } = this.extractOrderErrorMessage(error);

    this.channelEventsService
      .sendOrderFailedEvent({
        orderType,
        products,
        total,
        paymentMethod: this.orderEventMetodoPagamento,
        status: 'FAILED',
        errorMessage,
        errorCode,
      })
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        next: () => {},
        error: err => console.error('Erro ao enviar evento ORDER_FAILED:', err),
      });
  }

  private extractCreatedOrderUuid(response: unknown): string | undefined {
    if (!response || typeof response !== 'object') {
      return undefined;
    }

    const body = response as {
      uuid?: unknown;
      orderUuid?: unknown;
      data?: { uuid?: unknown; orderUuid?: unknown } | null;
    };

    const candidates = [
      body.data?.uuid,
      body.data?.orderUuid,
      body.uuid,
      body.orderUuid,
    ];

    const uuid = candidates.find(
      (value): value is string => typeof value === 'string' && value.trim().length > 0
    );

    return uuid?.trim();
  }

  private sendChannelEvents(
    orderResponse: unknown,
    orderRequest: CreateOrderRequest,
    cartItemsOverride?: CartItem[]
  ): void {
    const orderUuid = this.extractCreatedOrderUuid(orderResponse);
    if (!orderUuid) {
      return;
    }

    const cartItems = cartItemsOverride ?? this.getCartItems();

    const products = this.mapCartItemsToOrderEventProducts(cartItems);

    const total = products.reduce(
      (sum, prod) => sum + prod.amount * prod.quantity,
      0
    );

    this.channelEventsService
      .sendOrderCreatedEvent({
        orderUuid,
        total,
        paymentMethod: this.orderEventMetodoPagamento,
        status: 'PENDING',
        products,
        sellerCode: orderRequest.sellerCode || undefined,
      })
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        next: () => {},
        error: err => console.error('Erro ao enviar evento ORDER_CREATED:', err),
      });
  }

  private sendMindChannelEvents(mindOrderResponse: MindOrderCreateResponse, cartItemsOverride?: CartItem[]): void {
    if (!mindOrderResponse?.orderUuid) {
      return;
    }

    const cartItems = cartItemsOverride ?? this.getCartItems();
    const products = this.mapCartItemsToOrderEventProducts(cartItems);
    const total = products.reduce(
      (sum, prod) => sum + prod.amount * prod.quantity,
      0
    );
    this.channelEventsService
      .sendOrderCreatedEvent({
        orderUuid: mindOrderResponse.orderUuid,
        total,
        paymentMethod: this.orderEventMetodoPagamento,
        status: mindOrderResponse.status,
        products,
        sellerCode: mindOrderResponse.sellerCode ?? undefined,
      })
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        next: () => {},
        error: err => console.error('Erro ao enviar evento ORDER_CREATED (Mind):', err),
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  onTermsAcceptedChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.termsAccepted = target.checked;
  }

  get isPaymentFormValid(): boolean {
    return this.paymentInvoiceCard?.showCheck || false;
  }

  get isSubmitButtonDisabled(): boolean {
    const productConfigValid = this.showProductConfigCard
      ? this.productConfigCard?.showCheck
      : true;

    return (
      !this.termsAccepted || !productConfigValid || !this.isPaymentFormValid
    );
  }

  private validateChildComponents(): boolean {
    if (!this.isCompanyStepValid()) {
      this.toastService.showError(
        this.i18nService.instant('pages.checkout.messages.companyError'),
        5000,
        'top-center'
      );
      return false;
    }

    if (this.showProductConfigCard) {
      if (this.productConfigCard?.isLastStep && this.productConfigCard?.isEditing) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.configDomainError'),
          5000,
          'top-center'
        );
        return false;
      }

      if (!this.productConfigCard?.isFormValid()) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.configDomainError'),
          5000,
          'top-center'
        );
        return false;
      }

      if (!this.productConfigCard?.areAllDomainsValid()) {
        this.toastService.showError(
          this.i18nService.instant(
            'pages.checkout.messages.configValidateError'
          ),
          5000,
          'top-center'
        );
        return false;
      }
    }

    if (!this.paymentInvoiceCard?.showCheck) {
      this.toastService.showError(
        this.i18nService.instant('pages.checkout.messages.paymentError'),
        5000,
        'top-center'
      );
      return false;
    }

    if (this.isInvoicePaymentBlocked()) {
      this.showInvoiceUnavailableErrorToast();
      return false;
    }

    if (!this.validateMceLinkedMsisdns()) {
      return false;
    }

    if (this.isMinhaClaroSelected && !this.isMceExternalFlow) {
      if (!this.productLinkCard?.isFormValid()) {
        this.toastService.showError(
          this.i18nService.instant('pages.checkout.messages.linkingError'),
          5000,
          'top-center'
        );
        return false;
      }
    }

    return true;
  }

  private buildCreateOrderRequest():
    | { orderRequest: CreateOrderRequest; type: 'SVA_ORDER' }
    | { orderRequest: MindOrderCreateRequest; type: 'MIND_ORDER' } {
    const user = this.getCurrentUser();
    const cartItems = this.getCartItems();
    const primaryAddress = this.getPrimaryAddress(user);

    if (!user?.uuid) {
      throw new Error('Usuário não encontrado');
    }

    if (!primaryAddress) {
      throw new Error('Endereço não encontrado');
    }

    if (this.hasMindItemsInCart) {
      return {
        orderRequest: this.buildMindOrderRequestFromCart(this.getMindCartItems()),
        type: 'MIND_ORDER',
      };
    } else {
      const selectedContract = this.getSelectedContract();
      const orderRequest: CreateOrderRequest = {
        customerUuid: user.uuid,
        salespersonUuid: 'ceaf758a-de77-429f-b2f7-071984372deb',
        fluxo: this.isMinhaClaroSelected ? 'MOVEL' : this.isFixaSelected ? 'FIXA' : 'BOLETO_CARTAO',
        cardToken: this.getCardToken(),
        msisdn: user.phone || '',
        products: this.buildProducts(cartItems),
        payments: this.buildPayments(cartItems),
        billingAddress: this.buildBillingAddress(primaryAddress),
        urlDomain: this.urlService.getOrigin(),
        ordersEmailRequest: {
          domainEmail: 'CLARO',
        },
        sellerCode:
          this.paymentInvoiceCard?.sellerForm?.get('sellerCode')?.value ??
          this.savedSellerCode ??
          null,
        recurring: false,
        acceptanceTermId: this.acceptanceTermId,
        ipAddress: this.paymentInvoiceCard?.deviceInfo?.ip || '',
        customerAccountId: selectedContract?.customerAccountId ?? null,
        operatorCode: selectedContract?.operatorCode ?? null,
        contractId: selectedContract?.customerAccountId ?? null,
      };

      this.applyOriginChannelToOrderRequest(orderRequest);

      return { orderRequest, type: 'SVA_ORDER' };
    }
  }

  private buildMindOrderRequest(): MindOrderCreateRequest {
    return this.buildMindOrderRequestFromCart(this.getMindCartItems());
  }

  private buildMindOrderRequestFromCart(
    mindItems: CartItem[]
  ): MindOrderCreateRequest {
    const user = this.getCurrentUser();
    const primaryAddress = this.getPrimaryAddress(user);
    if (!user?.uuid) throw new Error('Usuário não encontrado');
    if (!primaryAddress) throw new Error('Endereço não encontrado');

    const homePass = (this.savedInstallationForOrder?.homePass ?? '').trim();
    if (!homePass) {
      throw new Error('Home Pass não encontrado');
    }

    const location = this.getSavedLocation();
    const zipcode =
      this.savedInstallationForOrder?.installationAddress?.zipcode ??
      location?.cep ??
      primaryAddress.zipCode ??
      '';
    const number =
      this.savedInstallationForOrder?.installationAddress?.number ??
      location?.addressNumber ??
      String(primaryAddress.number ?? '');

    const installationAddress = buildInstallationAddress({
      zipcode,
      number,
      district:
        location?.neighborhood ?? primaryAddress.neighborhood ?? null,
      complement: this.savedAddressDetails?.complement ?? null,
    });

    const products = this.buildMindOrderProducts(mindItems);
    const installationFee = mindItems.reduce((max, item) => {
      const meta = parseMindOrderMeta(item.product?.customFields);
      return Math.max(max, meta?.installationFee ?? 0);
    }, 0);

    const first = this.savedScheduleSelections?.firstOption;
    const second = this.savedScheduleSelections?.secondOption;
    if (!first || !second) {
      throw new Error('Agendamentos de instalação não encontrados');
    }

    const mindCustomer =
      this.companyCard?.getMindCustomerSnapshot?.() ?? this.savedMindCustomer;

    if (mindCustomer) {
      this.savedMindCustomer = mindCustomer;
    }

    const contactName = (
      mindCustomer?.contactName ||
      `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
    ).trim();

    const customer = buildMindCustomer({
      contactName,
      document: mindCustomer?.document || user.document || '',
      companyName: mindCustomer?.companyName || user.business || '',
      email: mindCustomer?.email || user.email || '',
      cpf: mindCustomer?.cpf || '',
      marketingDelivery: mindCustomer?.marketingDelivery,
    });

    return buildMindOrderCreateRequest({
      userUuid: user.uuid,
      regionId: this.getRegionId(),
      homePass,
      installationAddress,
      products,
      schedule: [
        mapScheduleSlotToOrderSchedule(first),
        mapScheduleSlotToOrderSchedule(second),
      ],
      customer,
      installationFee,
      affiliateId:
        this.channelBrandService.affiliateId ||
        this.channelData?.affiliateId,
    });
  }

  private buildSvaOrderRequest(svaItems: CartItem[]): CreateOrderRequest {
    const user = this.getCurrentUser();
    const selectedContract = this.getSelectedContract();
    const primaryAddress = this.getPrimaryAddress(user);
    if (!user?.uuid) throw new Error('Usuário não encontrado');
    if (!primaryAddress) throw new Error('Endereço não encontrado');
    const svaTotal = this.computeTotalForCartItems(svaItems);
    const orderRequest: CreateOrderRequest = {
      customerUuid: user.uuid,
      salespersonUuid: 'ceaf758a-de77-429f-b2f7-071984372deb',
      fluxo: this.isMinhaClaroSelected ? 'MOVEL' : this.isFixaSelected ? 'FIXA' : 'BOLETO_CARTAO',
      cardToken: this.getCardToken(),
      msisdn: user.phone || '',
      products: this.buildProducts(svaItems),
      payments: this.buildPayments(svaItems, svaTotal),
      billingAddress: this.buildBillingAddress(primaryAddress),
      urlDomain: this.urlService.getOrigin(),
      ordersEmailRequest: { domainEmail: 'CLARO' },
      sellerCode:
        this.paymentInvoiceCard?.sellerForm?.get('sellerCode')?.value ?? this.savedSellerCode ?? null,
      recurring: false,
      acceptanceTermId: this.acceptanceTermId,
      ipAddress: this.paymentInvoiceCard?.deviceInfo?.ip || '',
      customerAccountId: selectedContract?.customerAccountId ?? null,
      operatorCode: selectedContract?.operatorCode ?? null,
      contractId: selectedContract?.customerAccountId ?? null,
    };
    this.applyOriginChannelToOrderRequest(orderRequest);
    return orderRequest;
  }

  private getExternalChannelOrderOrigin(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return undefined;
    }

    if (!readExternalChannelAccessFromSession()) {
      return undefined;
    }

    const origin = readStoredExternalChannelOrigin();
    return origin || undefined;
  }

  private applyOriginChannelToOrderRequest(orderRequest: CreateOrderRequest): void {
    const externalOrigin = this.getExternalChannelOrderOrigin();
    if (externalOrigin) {
      orderRequest.originChannel = externalOrigin;
      return;
    }

    if (this.hasUtmInCurrentUrl()) {
      orderRequest.originChannel = 'DIGITAL_ALLIANCE';
    }
  }

  private buildMindOrderProducts(cartItems: CartItem[]): MindOrderProduct[] {
    return cartItems
      .filter(item => this.isMindCartItem(item))
      .map(item => buildMindOrderProductFromCart(item.product));
  }

  private buildMindScheduleProducts(
    cartItems: CartItem[]
  ): MindScheduleProduct[] {
    return cartItems
      .filter(item => this.isMindCartItem(item))
      .map(item => buildMindScheduleProductFromCart(item.product));
  }

  private getSavedLocation(): SavedLocation | null {
    let location: SavedLocation | null = null;
    this.locationFacade.location$
      .subscribe(loc => {
        location = loc;
      })
      .unsubscribe();
    return location;
  }

  private hasUtmInCurrentUrl(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      return params.has('utm_source');
    } catch {
      return false;
    }
  }

  private getRegionId(): string {
    let regionId = '';
    this.regionId$.subscribe(id => (regionId = id)).unsubscribe();
    return regionId;
  }

  private getCurrentUser(): User | null {
    let user: User | null = null;
    this.currentUser$.subscribe(u => (user = u)).unsubscribe();
    return user;
  }

  private getSelectedContract(): FixaResource | null {
    let contract: FixaResource | null = null;
    this.userFacade.getSelectedContract().subscribe(c => (contract = c)).unsubscribe();
    return contract;
  }

  private getSelectedContractDetails(): SelectedContractDetails | null {
    let details: SelectedContractDetails | null = null;
    this.userFacade.getSelectedContractDetails().subscribe(d => (details = d)).unsubscribe();
    return details;
  }

  private getCartItems(): CartItem[] {
    let items: CartItem[] = [];
    this.cartFacade
      .getCartItems()
      .subscribe((cartItems: CartItem[]) => {
        items = cartItems;
      })
      .unsubscribe();
    return items;
  }

  private getMindCartItems(): CartItem[] {
    return this.getCartItems().filter(item => this.isMindCartItem(item));
  }

  private getSvaCartItems(): CartItem[] {
    return this.getCartItems().filter(item => !this.isMindCartItem(item));
  }

  private hasPresencaDigitalInCart(): boolean {
    return this.getCartItems().some(item => {
      const variationUuid = item.selectedVariation?.uuid;
      return (
        Boolean(variationUuid) && PRESENCA_DIGITAL_UUIDS.includes(variationUuid as string)
      );
    });
  }

  private hasGoogleNewDomainRegistrationInCart(): boolean {
    return this.getCartItems().some(item => {
      const fields = [
        ...(item.product?.requiredFields ?? []),
        ...(item.selectedVariation?.requiredFields ?? []),
      ];
      const hasGoogleDomain = fields.some(
        f => f.name === 'google_ws_domain' && typeof f.value === 'string' && f.value.trim().length > 0
      );
      if (!hasGoogleDomain) {
        return false;
      }
      const preference = fields
        .find(f => f.name === 'domain_preference')
        ?.value?.toString()
        .trim()
        .toLowerCase();
      return preference === 'new';
    });
  }

  private hasGoogleNewDomainInOrderProducts(
    products: { requiredFields?: { name: string; value?: string }[] }[] | undefined
  ): boolean {
    if (!products?.length) {
      return false;
    }
    return products.some(product => {
      const fields = product.requiredFields ?? [];
      const hasGoogleDomain = fields.some(
        f => f.name === 'google_ws_domain' && typeof f.value === 'string' && f.value.trim().length > 0
      );
      if (!hasGoogleDomain) {
        return false;
      }
      const preference = fields
        .find(f => f.name === 'domain_preference')
        ?.value?.toString()
        .trim()
        .toLowerCase();
      return preference === 'new';
    });
  }

  private hasEmailEmpresarialDomainRegistrationInCart(): boolean {
    return this.getCartItems().some(item => {
      if (!isEmailEmpresarialVariationUuid(item.selectedVariation?.uuid)) {
        return false;
      }
      const fields = [
        ...(item.product?.requiredFields ?? []),
        ...(item.selectedVariation?.requiredFields ?? []),
      ];
      return fields.some(
        f =>
          f.name === EMAIL_DOMAIN_FIELD_NAME &&
          typeof f.value === 'string' &&
          f.value.trim().length > 0
      );
    });
  }

  private hasEmailEmpresarialDomainInOrderProducts(
    products:
      | { uuid?: string; requiredFields?: { name: string; value?: string }[] }[]
      | undefined
  ): boolean {
    if (!products?.length) {
      return false;
    }
    return products.some(product => {
      if (!isEmailEmpresarialVariationUuid(product.uuid)) {
        return false;
      }
      const fields = product.requiredFields ?? [];
      return fields.some(
        f =>
          f.name === EMAIL_DOMAIN_FIELD_NAME &&
          typeof f.value === 'string' &&
          f.value.trim().length > 0
      );
    });
  }

  private shouldValidateOrderForDomainRegistration(
    orderProducts?: { uuid?: string; requiredFields?: { name: string; value?: string }[] }[]
  ): boolean {
    return (
      this.hasPresencaDigitalInCart() ||
      this.hasGoogleNewDomainRegistrationInCart() ||
      this.hasGoogleNewDomainInOrderProducts(orderProducts) ||
      this.hasEmailEmpresarialDomainRegistrationInCart() ||
      this.hasEmailEmpresarialDomainInOrderProducts(orderProducts)
    );
  }

  private isMindCartItem(item: CartItem): boolean {
    return (item.productType ?? item.product?.productType) === 'MIND';
  }

  private computeTotalForCartItems(items: CartItem[]): number {
    return items.reduce((total, item) => {
      const price =
        item.selectedVariation?.salesPrice ??
        item.selectedVariation?.price ??
        item.product.salesPrice ??
        item.product.price ??
        0;
      return total + price * item.quantity;
    }, 0);
  }

  private getCardToken(): string | null {
    if (this.invoicePaymentType === 'minha_conta') {
      return null;
    }
    if (this.paymentInvoiceCard?.selectedPaymentMethod === 'credit_card') {
      return this.paymentInvoiceCard?.selectedCardId;
    }
    return null;
  }

  private getPrimaryAddress(user: User | null): User['addresses'][0] | null {
    if (!user?.addresses?.length) return null;
    return user.addresses.find(a => a.main) ?? user.addresses[0];
  }

  private getCoupon(): string {
    if (this.isFixaSelected) {
      return 'PROMOHUBPMC';
    }
    if (this.invoicePaymentType === 'minha_conta') {
      return '';
    }
    if (this.paymentInvoiceCard?.selectedPaymentMethod === 'credit_card') {
      return 'PROMOHUBPMC';
    }
    return '';
  }

  private buildProducts(cartItems: CartItem[]): CreateOrderProduct[] {
    return cartItems.flatMap(item => {
      const variationUuid = item.selectedVariation?.uuid;
      const isPresencaDigital =
        Boolean(variationUuid) && PRESENCA_DIGITAL_UUIDS.includes(variationUuid as string);

      // PRESENCA_DIGITAL_UUIDS: quando quantity > 1, enviar N products iguais
      // (mesmo uuid) com required_fields diferentes.
      if (isPresencaDigital) {
        return Array.from({ length: item.quantity }, (_, domainFormIndex) => ({
          uuid: variationUuid as string,
          quantity: 1,
          variable: true,
          coupon: this.getCoupon(),
          requiredFields: this.buildRequiredFields(item, domainFormIndex),
          origin: 'Odin',
          terms: item.product.terms,
          msisdn: item.linkedMsisdns || [],
          ...this.buildSelfConfirmedOwnershipField(item, domainFormIndex),
          ...this.buildConfirmedNoOtherGoogleAccountField(item, domainFormIndex),
        }));
      }

      // Regra padrão: 1 entry com quantity N e required_fields do input único.
      return [
        {
          uuid: variationUuid || item.product.uuid,
          quantity: item.quantity,
          variable: true,
          coupon: this.getCoupon(),
          requiredFields: this.buildRequiredFields(item, 0),
          origin: 'Odin',
          terms: item.product.terms,
          msisdn: item.linkedMsisdns || [],
          ...this.buildSelfConfirmedOwnershipField(item, 0),
          ...this.buildConfirmedNoOtherGoogleAccountField(item, 0),
        },
      ];
    });
  }

  private getProductConfigFormData(
    cartItem: CartItem,
    domainFormIndex: number
  ) {
    const variationUuid = cartItem.selectedVariation?.uuid;
    if (!variationUuid) return null;

    return (
      this.productConfigCard?.getUnifiedFormDataByBaseVariationUuidAndCartItemIdAndIndex(
        variationUuid,
        cartItem.id,
        domainFormIndex
      ) ??
      this.productConfigCard?.getUnifiedFormDataByBaseVariationUuidAndIndex(
        variationUuid,
        domainFormIndex
      ) ??
      null
    );
  }

  private buildSelfConfirmedOwnershipField(
    cartItem: CartItem,
    domainFormIndex: number
  ): { selfConfirmedOwnership: boolean } | Record<string, never> {
    const formData = this.getProductConfigFormData(cartItem, domainFormIndex);
    const domainFormKey =
      formData?.variationUuid ??
      this.resolveDomainFormKey(cartItem, domainFormIndex);
    const domainType =
      formData?.domainType ??
      this.resolveDomainTypeFromCartItem(cartItem, domainFormIndex);
    const group =
      formData?.group ?? this.resolveDomainGroupFromCartItem(cartItem);

    if (!domainFormKey || domainType !== 'existing' || group === 'microsoft') {
      return {};
    }

    const selfConfirmedOwnership =
      this.productConfigCard?.isSelfConfirmedOwnership(domainFormKey ?? undefined) ??
      (domainFormKey
        ? ProductConfigCardComponent.isSelfConfirmedOwnershipCached(domainFormKey)
        : false);

    // Sem form vivo, só envia se o cache tiver confirmação (evita false positivo no create).
    if (!formData && !selfConfirmedOwnership) {
      return {};
    }

    return { selfConfirmedOwnership };
  }

  private buildConfirmedNoOtherGoogleAccountField(
    cartItem: CartItem,
    domainFormIndex: number
  ): { confirmedNoOtherGoogleAccount: boolean } | Record<string, never> {
    const formData = this.getProductConfigFormData(cartItem, domainFormIndex);
    const domainFormKey =
      formData?.variationUuid ??
      this.resolveDomainFormKey(cartItem, domainFormIndex);
    const domainType =
      formData?.domainType ??
      this.resolveDomainTypeFromCartItem(cartItem, domainFormIndex);
    const group =
      formData?.group ?? this.resolveDomainGroupFromCartItem(cartItem);

    const isSelfConfirmedOwnership =
      this.productConfigCard?.isSelfConfirmedOwnership(domainFormKey ?? undefined) ??
      (domainFormKey
        ? ProductConfigCardComponent.isSelfConfirmedOwnershipCached(domainFormKey)
        : false);

    if (
      !domainFormKey ||
      group !== 'google' ||
      domainType !== 'existing' ||
      !isSelfConfirmedOwnership
    ) {
      return {};
    }

    return {
      confirmedNoOtherGoogleAccount:
        this.productConfigCard?.isGoogleDomainNotInUseAcknowledged(domainFormKey) ??
        ProductConfigCardComponent.isGoogleDomainNotInUseAcknowledgedCached(
          domainFormKey
        ),
    };
  }

  private resolveDomainFormKey(
    cartItem: CartItem,
    domainFormIndex: number
  ): string | null {
    const baseVariationUuid = cartItem.selectedVariation?.uuid;
    if (!baseVariationUuid || !cartItem.id) {
      return null;
    }
    return `${baseVariationUuid}__${cartItem.id}__${domainFormIndex}`;
  }

  private resolveDomainTypeFromCartItem(
    cartItem: CartItem,
    domainFormIndex: number
  ): 'new' | 'existing' | null {
    const fields = [
      ...(cartItem.product?.requiredFields ?? []),
      ...(cartItem.selectedVariation?.requiredFields ?? []),
    ];
    const matches = fields.filter(f => f.name === 'domain_preference' || f.name === 'tenant_preference');
    const raw = (matches[domainFormIndex] ?? matches[0])?.value;
    const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (value === 'new' || value === 'existing') {
      return value;
    }
    return null;
  }

  private resolveDomainGroupFromCartItem(
    cartItem: CartItem
  ): 'google' | 'microsoft' | 'other' {
    const fields = [
      ...(cartItem.product?.requiredFields ?? []),
      ...(cartItem.selectedVariation?.requiredFields ?? []),
    ];
    if (fields.some(f => f.name === 'google_ws_domain')) {
      return 'google';
    }
    if (fields.some(f => f.name === 'microsoft_domain')) {
      return 'microsoft';
    }
    return 'other';
  }

  private buildRequiredFields(
    cartItem: CartItem,
    domainFormIndex: number
  ): OrderRequiredField[] {
    const requiredFields: OrderRequiredField[] = [];

    const variationUuid = cartItem.selectedVariation?.uuid;
    if (!variationUuid) return requiredFields;

    const formData = this.getProductConfigFormData(cartItem, domainFormIndex);

    if (formData?.domain) {
      const completeDomain = `${formData.domain}${this.productConfigCard?.getDomainSuffixByGroup(formData.group, formData.domainType) || ''}`;

      if (formData.group === 'com.br') {
        const domainPreferenceValue = (formData.domainType === 'existing' || formData.domainType === 'new')
        ? formData.domainType
        : 'new';
        const domainFieldName = isEmailEmpresarialVariationUuid(variationUuid)
          ? EMAIL_DOMAIN_FIELD_NAME
          : 'com_br_domain';
        requiredFields.push(
          {
          name: domainFieldName,
          label: domainFieldName,
          type: 'TEXT',
          value: completeDomain,
          }, {
            name: 'domain_preference',
            label: 'domain_preference',
            type: 'TEXT',
            value: domainPreferenceValue,
          }
        );
      }
      if (formData.group === 'com') {
        requiredFields.push({
          name: 'com_domain',
          label: 'com_domain',
          type: 'TEXT',
          value: completeDomain,
        });
      }

      if (formData.group === 'net') {
        requiredFields.push({
          name: 'net_domain',
          label: 'net_domain',
          type: 'TEXT',
          value: completeDomain,
        });
      }

      if (formData.group === 'google') {
        const domainPreferenceValue =
          formData.domainType === 'existing' || formData.domainType === 'new'
            ? formData.domainType
            : 'new';

        requiredFields.push({
          name: 'google_ws_domain',
          label: 'Domínio Google Workspace',
          type: 'TEXT',
          value: completeDomain,
        });

        requiredFields.push({
          name: 'domain_preference',
          label: 'domain_preference',
          type: 'TEXT',
          value: domainPreferenceValue,
        });

        const adminField = cartItem.product.requiredFields?.find(
          f => f.name === 'google_ws_admin_email'
        );
        if (adminField) {
          requiredFields.push({
            name: 'google_ws_admin_email',
            label: 'Admin',
            type: 'TEXT',
            value: 'admin',
          });
        }
      } else if (formData.group === 'microsoft') {
        requiredFields.push({
          name: 'microsoft_domain',
          label: 'Domínio da Microsoft',
          type: 'TEXT',
          value: completeDomain,
        });

        // Busca o tenant_preference do produto no carrinho primeiro (fonte mais confiável)
        const tenantPrefField = cartItem.product.requiredFields?.find(
          f => f.name === 'tenant_preference' && f.value
        );

        let tenantPreference = formData.domainType || 'new';
        if (
          tenantPrefField?.value &&
          (tenantPrefField.value === 'new' ||
            tenantPrefField.value === 'existing')
        ) {
          tenantPreference = tenantPrefField.value;
        }

        requiredFields.push({
          name: 'tenant_preference',
          label: 'Tenant Preference',
          type: 'TEXT',
          value: tenantPreference,
        });

        const emailField = cartItem.product.requiredFields?.find(
          f => f.name === 'email_address_agreement'
        );
        if (emailField) {
          requiredFields.push({
            name: 'email_address_agreement',
            label: 'Email de Confirmação',
            type: 'TEXT',
            value: this.getCurrentUser()?.email || '',
          });
        }
      }
    }

    return requiredFields;
  }

  private buildPayments(cartItems: CartItem[], totalAmount?: number): CreateOrderPayment[] {
    const total = totalAmount ?? this.orderSummary?.totalValue ?? 0;

    const totalItems = cartItems.reduce((totalCount, item) => {
      return totalCount + item.quantity;
    }, 0);

    return [
      {
        uuid: 'd734b11b-487e-4ff0-a6c6-7fbde4b67af1',
        amount: total,
        totalInstallments: totalItems,
        paymentNotes: '',
      },
    ];
  }

  private buildBillingAddress(
    primaryAddress: User['addresses'][0]
  ): OrderBillingAddress {
    return {
      address: primaryAddress.address || '',
      number: primaryAddress.number || '',
      zip: primaryAddress.zipCode || '',
      neighborhood: primaryAddress.neighborhood || '',
      city: primaryAddress.city || '',
      country: primaryAddress.country || 'BR',
      state: primaryAddress.state || '',
      complement: primaryAddress.complement || '',
    };
  }

  private mergeOwnershipFieldsIntoSvaPayload(
    payload: CreateOrderRequest,
    freshProducts: CreateOrderProduct[]
  ): CreateOrderRequest {
    if (!payload.products?.length || !freshProducts.length) {
      return payload;
    }

    const products = payload.products.map((product, index) => {
      const fresh = freshProducts[index];
      if (!fresh) {
        return product;
      }

      return {
        ...product,
        ...('selfConfirmedOwnership' in fresh
          ? { selfConfirmedOwnership: fresh.selfConfirmedOwnership }
          : {}),
        ...('confirmedNoOtherGoogleAccount' in fresh
          ? { confirmedNoOtherGoogleAccount: fresh.confirmedNoOtherGoogleAccount }
          : {}),
      };
    });

    return { ...payload, products };
  }

  ngOnDestroy(): void {
    this.lastValidatedSvaPayload = null;
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
